// tasksApi — thin client for /api/tasks and friends. Mirrors the server
// PascalCase response shape so components can bind directly.

import { api, apiErrorMessage } from '@/lib/api';
import { DASHBOARD_PATHS, buildPath } from "./paths";

const TASK_DETAIL_CACHE_TTL_MS = 15_000;

const taskDetailCache = new Map<string, {
  t: number;
  value?: TaskDetail;
  promise?: Promise<TaskDetail>;
}>();

export interface TaskRequestOptions {
  /** Pass a project id to force X-Project-Id; pass '' to intentionally omit it. */
  projectId?: string;
}

export type TaskSourceKind =
  | 'crash' | 'exception' | 'anr' | 'gpu_hang' | 'broken_shader' | 'bug' | 'error_report'
  | 'feedback' | 'feedback_item' | 'chat' | 'goal';

export type TaskStatusCategory =
  | 'backlog' | 'unstarted' | 'started' | 'completed' | 'canceled';

export interface TaskStatus {
  Id: string;
  ProjectId: string;
  Name: string;
  Color: string;
  Category: TaskStatusCategory;
  SortOrder: number;
  IsIssueInbox: boolean;
  IsTriageInbox: boolean;
  IsQaSendBack: boolean;
  IsReadyForQa: boolean;
  IsTested: boolean;
  IsRegressionTarget: boolean;
  IsResolveTarget: boolean;
  CreatedAt: string;
}

export interface TaskFlag {
  Id: string;
  ProjectId: string;
  /** Lowercase machine key, e.g. "wip". Stored on tasks under `Flags`. */
  Key: string;
  Label: string;
  Color: string;
  /** When true, this flag is auto-added when a user assigns the task to themselves. */
  AutoOnSelfAssign: boolean;
  /** When true, this flag is auto-removed on transition into a status with IsReadyForQa. */
  ClearOnReadyForQa: boolean;
  SortOrder: number;
  CreatedAt: string;
}

export interface Task {
  Id: string;
  ProjectId: string;
  Title: string;
  Description: string;
  StatusId: string | null;
  Status: TaskStatus | null;
  Rank: string;
  AssigneeUserId: string | null;
  AssigneeName: string;
  PreferredNextUserId: string | null;
  PreferredNextName: string;
  CapableUserIds: string[];
  CapableNames: string[];
  QaAssigneeUserId: string | null;
  QaAssigneeName: string;
  QaClaimedAt: string | null;
  Labels: string[];
  /** Stackable flag keys (machine ids) drawn from the project's flag pool. */
  Flags: string[];
  SourceKind: TaskSourceKind | null;
  SourceId: string | null;
  RequesterEmail: string | null;
  RequesterName: string | null;
  RequesterChatThreadId: string | null;
  NotifyRequester: boolean;
  ParentTaskId: string | null;
  DueDate: string | null;
  EstimateHours: number | null;
  RemainingHours: number | null;
  ReleasedInBuildVersion: string | null;
  ReleaseNotifiedAt: string | null;
  TargetPlatform: string | null;
  CreatedByUserId: string | null;
  CreatedByName: string;
  CreatedAt: string;
  UpdatedAt: string;
  ResolvedAt: string | null;
  /** Urgency bucket the task lived in before being worked/QA'd. The QA
   *  queue sorts by this status's SortOrder as "priority". */
  PriorStatusId?: string | null;
  /** Latest changeset recorder, falling back to the 'resolved' activity
   *  actor. List-query only — absent on PATCH/broadcast payloads. */
  ResolvedByName?: string | null;
  LatestChangeset?: TaskChangesetSummary | null;
  IssueLinkCount: number;
  BlockerCount: number;
  SubscriberCount: number;
  ViewerSubscribed: boolean;
  CodeLinkCount: number;
  DuplicateScore?: number;
  /** Preview data assembled server-side for triage cards. Null for
   *  type='task' rows that have no upstream issue, or when no event
   *  has been ingested yet. */
  Preview?: TaskPreview | null;
  ExceptionType?: string | null;
  BlameFunction?: string | null;
  BlameLocation?: string | null;
}

export interface TaskChangesetSummary {
  Changeset: string;
  Branch: string | null;
  AuthorName: string | null;
  RecordedByName: string;
  Comment: string;
  CreatedAt: string;
}

export interface TaskPreview {
  /** First line of the stack-trace top frame, e.g. "NullReferenceException at Foo.Bar:42" */
  StackLine: string | null;
  /** Multi-line snippet (top 3 frames) when available — used for hover preview */
  StackSnippet: string | null;
  ExceptionMessage: string | null;
  ScreenshotUrl: string | null;
  VideoUrl: string | null;
  VideoPosterUrl: string | null;
  Platform: string | null;
  BuildVersion: string | null;
}

export interface TaskSource {
  Kind: TaskSourceKind;
  Id: string;
  Title: string;
  RequesterEmail: string | null;
  RequesterName: string | null;
  RequesterChatThreadId: string | null;
  DeepLink: string | null;
}

export interface TaskAttachment {
  Id: string;
  TaskId: string;
  Filename: string;
  Url: string;
  ContentType: string;
  Size: number;
  Bookmarks: TaskAttachmentBookmark[];
  UploadedByUserId: string | null;
  UploadedByName: string;
  CreatedAt: string;
  /** 'user' = uploaded via the task UI (mutable). 'issue_event' = pass-
   *  through reference to a file already attached to the source issue
   *  event; URL points at the existing /api/files/... blob, no copy.
   *  Read-only on the dashboard side — delete + bookmarks are disabled. */
  Source: 'user' | 'issue_event';
}

export interface TaskAttachmentBookmark {
  Id: string;
  TimeMs: number;
  Label: string;
  Primary: boolean;
}

export interface TaskActivity {
  Id: string;
  TaskId: string;
  ActorUserId: string | null;
  ActorName: string;
  Kind: string;
  FromValue: string | null;
  ToValue: string | null;
  CreatedAt: string;
}

export type IssueLinkType =
  | 'crash' | 'exception' | 'anr' | 'gpu_hang' | 'broken_shader' | 'bug' | 'error_report'
  | 'feedback' | 'feedback_item' | 'chat'
  // 'goal' is a TaskSourceKind but never a real issue_link row — server
  // rejects link-existing for it. Kept in the union purely so the
  // TaskSourceKind → IssueLinkType cast in ConvertToTaskButton compiles
  // without needing a runtime exception path.
  | 'goal';

export type IssueLinkRelation = 'related' | 'caused_by';

export interface TaskIssueLink {
  Id: string;
  TaskId: string;
  IssueType: IssueLinkType;
  IssueId: string;
  IsSource: boolean;
  Relation: IssueLinkRelation;
  CreatedAt: string;
}

export interface TaskDetail extends Task {
  Source: TaskSource | null;
  Parent: Task | null;
  Children: Task[];
  Blockers: Task[];
  Blocking: Task[];
  /** Tasks that historically caused this task (no workflow gating). */
  Causes: Task[];
  /** Tasks this task historically caused. */
  Caused: Task[];
  Attachments: TaskAttachment[];
  Documents: TaskDocument[];
  Activity: TaskActivity[];
  IssueLinks: TaskIssueLink[];
}

export interface TaskDocument {
  Id: string;
  TaskId: string;
  Url: string;
  Title: string;
  AddedByUserId: string | null;
  AddedByName: string;
  CreatedAt: string;
}

export interface CommentSource {
  type: 'task' | IssueLinkType;
  id: string;
  kind: 'self' | 'source' | 'linked-issue' | 'linked-task';
}

export interface TaskComment {
  id: string;
  author: string;
  authorEmail: string;
  authorId: string;
  body: string;
  createdAt: string;
  syncSource: 'bugpunch' | 'chat' | 'email';
  visibility: 'internal' | 'public';
  source?: CommentSource;
  reactions?: TaskCommentReaction[];
}
export type CommentIssueType = CommentSource['type'];

export interface TaskCommentReaction {
  Emoji: string;
  Count: number;
  ReactedByMe: boolean;
}

export interface Assignee { Id: string; Email: string; DisplayName: string }

async function json<T>(url: string, init?: RequestInit, options: TaskRequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) };
  // `projectId` undefined → let the client default to the active project.
  // '' → intentional omit (the client deletes an empty X-Project-Id).
  if (options.projectId !== undefined) headers['X-Project-Id'] = options.projectId;
  // Call sites pass `body: JSON.stringify(...)`; flag it as JSON so ofetch
  // doesn't ship a string body without a Content-Type.
  if (typeof init?.body === 'string' && !('Content-Type' in headers)) headers['Content-Type'] = 'application/json';
  try {
    return await api<T>(url, { method: init?.method, body: init?.body as BodyInit | undefined, headers });
  } catch (e) {
    throw new Error(apiErrorMessage(e));
  }
}

function invalidateTaskDetail(id: string): void {
  taskDetailCache.delete(id);
}

// ── Statuses ────────────────────────────────────────────────────────────────

export function listStatuses(options: TaskRequestOptions = {}): Promise<{ items: TaskStatus[] }> {
  return json(DASHBOARD_PATHS.tasksStatuses, undefined, options);
}
export function createStatus(body: {
  name: string; color: string; category: TaskStatusCategory; sortOrder?: number;
  isIssueInbox?: boolean; isTriageInbox?: boolean; isQaSendBack?: boolean; isReadyForQa?: boolean; isTested?: boolean;
  isRegressionTarget?: boolean;
}): Promise<TaskStatus> {
  return json(DASHBOARD_PATHS.tasksStatuses, { method: 'POST', body: JSON.stringify(body) });
}
export function updateStatus(id: string, body: Partial<{
  name: string; color: string; category: TaskStatusCategory; sortOrder: number;
  isIssueInbox: boolean; isTriageInbox: boolean; isQaSendBack: boolean; isReadyForQa: boolean; isTested: boolean;
  isRegressionTarget: boolean;
}>): Promise<TaskStatus> {
  return json(buildPath(DASHBOARD_PATHS.tasksStatusesById, { id }), { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteStatus(
  id: string,
  options: { reassignToStatusId?: string } = {},
): Promise<{ ok: boolean; reassigned?: number }> {
  const qs = options.reassignToStatusId
    ? `?reassignTo=${encodeURIComponent(options.reassignToStatusId)}`
    : '';
  return json(`${buildPath(DASHBOARD_PATHS.tasksStatusesById, { id })}${qs}`, { method: 'DELETE' });
}

// ── Flags ────────────────────────────────────────────────────────────────────

export function listFlags(options: TaskRequestOptions = {}): Promise<{ items: TaskFlag[] }> {
  return json(DASHBOARD_PATHS.tasksFlags, undefined, options);
}
export function createFlag(body: {
  key: string; label: string; color: string;
  autoOnSelfAssign?: boolean; clearOnReadyForQa?: boolean; sortOrder?: number;
}): Promise<TaskFlag> {
  return json(DASHBOARD_PATHS.tasksFlags, { method: 'POST', body: JSON.stringify(body) });
}
export function updateFlag(id: string, body: Partial<{
  label: string; color: string; sortOrder: number;
  autoOnSelfAssign: boolean; clearOnReadyForQa: boolean;
}>): Promise<TaskFlag> {
  return json(buildPath(DASHBOARD_PATHS.tasksFlagsById, { id }), {
    method: 'PATCH', body: JSON.stringify(body),
  });
}
export function deleteFlag(id: string): Promise<{ ok: boolean; purgedFrom: number }> {
  return json(buildPath(DASHBOARD_PATHS.tasksFlagsById, { id }), { method: 'DELETE' });
}

// ── Tasks ───────────────────────────────────────────────────────────────────

export interface ListTasksParams {
  projectId?: string;
  q?: string;
  assignee?: string;
  label?: string;
  statusIds?: string[];
  sourceKind?: TaskSourceKind;
  sourceId?: string;
  limit?: number;
  offset?: number;
}
export function listTasks(params: ListTasksParams = {}): Promise<{
  items: Task[]; total: number; statuses: TaskStatus[];
}> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.assignee) qs.set('assignee', params.assignee);
  if (params.label) qs.set('label', params.label);
  if (params.sourceKind) qs.set('sourceKind', params.sourceKind);
  if (params.sourceId) qs.set('sourceId', params.sourceId);
  if (params.statusIds && params.statusIds.length) qs.set('statusIds', params.statusIds.join(','));
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const q = qs.toString();
  return json(q ? `${DASHBOARD_PATHS.tasks}?${q}` : DASHBOARD_PATHS.tasks, undefined, {
    projectId: params.projectId,
  });
}

export function getTriageBadgeCount(options: TaskRequestOptions = {}): Promise<{ count: number }> {
  return json(DASHBOARD_PATHS.tasksTriageBadgeCount, undefined, options);
}

export function getTask(id: string): Promise<TaskDetail> {
  const cached = taskDetailCache.get(id);
  if (cached && Date.now() - cached.t < TASK_DETAIL_CACHE_TTL_MS) {
    if (cached.value) return Promise.resolve(cached.value);
    if (cached.promise) return cached.promise;
  }

  const promise = json<TaskDetail>(buildPath(DASHBOARD_PATHS.tasksById, { id }))
    .then((value) => {
      taskDetailCache.set(id, { t: Date.now(), value });
      return value;
    })
    .catch((error) => {
      invalidateTaskDetail(id);
      throw error;
    });
  taskDetailCache.set(id, { t: Date.now(), promise });
  return promise;
}

export function prefetchTask(id: string): Promise<TaskDetail> {
  return getTask(id);
}

export interface CreateTaskBody {
  title: string;
  description?: string;
  statusId?: string;
  assigneeUserId?: string | null;
  assigneeName?: string;
  preferredNextUserId?: string | null;
  preferredNextName?: string;
  capableUserIds?: string[];
  capableNames?: string[];
  qaAssigneeUserId?: string | null;
  qaAssigneeName?: string;
  labels?: string[];
  sourceKind?: TaskSourceKind;
  sourceId?: string;
  notifyRequester?: boolean;
  parentTaskId?: string;
  dueDate?: string | null;
  estimateHours?: number | null;
  remainingHours?: number | null;
  targetPlatform?: string | null;
  checkDuplicates?: boolean;
  force?: boolean;
  duplicateThreshold?: number;
}

export interface TaskDuplicateSuggestions {
  DuplicateCheck: true;
  Threshold: number;
  Suggestions: Task[];
}

export function isTaskDuplicateSuggestions(value: Task | TaskDuplicateSuggestions): value is TaskDuplicateSuggestions {
  return !!value && 'DuplicateCheck' in value && value.DuplicateCheck === true;
}

export function createTask(
  body: CreateTaskBody & { checkDuplicates: true; force?: false },
  options?: TaskRequestOptions,
): Promise<Task | TaskDuplicateSuggestions>;
export function createTask(body: CreateTaskBody, options?: TaskRequestOptions): Promise<Task>;
export function createTask(body: CreateTaskBody, options: TaskRequestOptions = {}): Promise<Task | TaskDuplicateSuggestions> {
  return json(DASHBOARD_PATHS.tasks, { method: 'POST', body: JSON.stringify(body) }, options);
}

export function convertToTask(_body: {
  sourceKind: TaskSourceKind;
  sourceId: string;
  title?: string;
  description?: string;
  notifyRequester?: boolean;
}): Promise<Task & { AlreadyExisted?: boolean }> {
  // Endpoint removed server-side as part of the task↔issue-link decoupling.
  // Callers still exist on the web side; stub here so the build passes and
  // any accidental invocation surfaces loudly at runtime rather than 404ing
  // silently through a stale path constant.
  return Promise.reject(new Error('convertToTask: endpoint removed'));
}

export function updateTask(id: string, patch: Partial<{
  title: string; description: string;
  statusId: string;
  assigneeUserId: string | null; assigneeName: string;
  preferredNextUserId: string | null; preferredNextName: string;
  capableUserIds: string[]; capableNames: string[];
  qaAssigneeUserId: string | null; qaAssigneeName: string;
  labels: string[]; flags: string[]; notifyRequester: boolean;
  parentTaskId: string | null;
  dueDate: string | null;
  estimateHours: number | null;
  remainingHours: number | null;
  releasedInBuildVersion: string | null;
  forceCompleteWithBlockers: boolean;
  /** Stated reason for sending to QA with no changeset linked — the server
   *  otherwise rejects the transition with QA_REQUIRES_CHANGESET. */
  qaWithoutChangesetReason: string;
  prevRank: string | null; nextRank: string | null;
}>): Promise<Task> {
  invalidateTaskDetail(id);
  return json<Task>(buildPath(DASHBOARD_PATHS.tasksById, { id }), { method: 'PATCH', body: JSON.stringify(patch) })
    .finally(() => invalidateTaskDetail(id));
}

// ── Dependencies ──────────────────────────────────────────────────────────

/** Record that `causeTaskId` historically caused this task. */
export function addCause(taskId: string, causeTaskId: string): Promise<{ id: string }> {
  return json(buildPath(DASHBOARD_PATHS.tasksByIdCauses, { id: taskId }), {
    method: 'POST',
    body: JSON.stringify({ causeTaskId }),
  });
}
/** Record that this task caused `effectTaskId`. */
export function addCaused(taskId: string, effectTaskId: string): Promise<{ id: string }> {
  return json(buildPath(DASHBOARD_PATHS.tasksByIdCaused, { id: taskId }), {
    method: 'POST',
    body: JSON.stringify({ effectTaskId }),
  });
}
// ── Release notification ──────────────────────────────────────────────────

export function notifyRelease(buildVersion: string):
  Promise<{ candidates: number; sent: number; failures: { taskId: string; error: string }[] }> {
  return json(DASHBOARD_PATHS.tasksNotifyRelease, {
    method: 'POST',
    body: JSON.stringify({ buildVersion }),
  });
}

export function deleteTask(id: string): Promise<{ ok: boolean }> {
  invalidateTaskDetail(id);
  return json<{ ok: boolean }>(buildPath(DASHBOARD_PATHS.tasksById, { id }), { method: 'DELETE' })
    .finally(() => invalidateTaskDetail(id));
}

// ── Attachments ─────────────────────────────────────────────────────────────

export async function uploadAttachment(taskId: string, file: File): Promise<TaskAttachment> {
  const fd = new FormData();
  fd.append('file', file);
  try {
    // FormData body: the client injects auth + active project; ofetch leaves
    // Content-Type to the browser so the multipart boundary is set.
    return await api(buildPath(DASHBOARD_PATHS.tasksByIdAttachments, { id: taskId }), { method: 'POST', body: fd });
  } catch (e) {
    throw new Error(apiErrorMessage(e));
  }
}

export function deleteAttachment(taskId: string, attId: string): Promise<{ ok: boolean }> {
  return json(buildPath(DASHBOARD_PATHS.tasksByIdAttachmentsByAttId, { id: taskId, attId }), { method: 'DELETE' });
}

export function updateAttachmentBookmarks(
  taskId: string,
  attId: string,
  bookmarks: TaskAttachmentBookmark[],
): Promise<TaskAttachment> {
  return json(buildPath(DASHBOARD_PATHS.tasksByIdAttachmentsByAttId, { id: taskId, attId }), {
    method: 'PATCH',
    body: JSON.stringify({ bookmarks }),
  });
}

// ── Documents (external URLs — TriliumNext notes today) ─────────────────────

export async function addTaskDocument(taskId: string, url: string, title: string): Promise<TaskDocument> {
  const doc = await json<TaskDocument>(buildPath(DASHBOARD_PATHS.tasksByIdDocuments, { id: taskId }), {
    method: 'POST',
    body: JSON.stringify({ url, title }),
  });
  invalidateTaskDetail(taskId);
  return doc;
}

export function deleteTaskDocument(taskId: string, docId: string): Promise<{ ok: boolean }> {
  return json<{ ok: boolean }>(
    buildPath(DASHBOARD_PATHS.tasksByIdDocumentsByDocId, { id: taskId, docId }),
    { method: 'DELETE' },
  ).finally(() => invalidateTaskDetail(taskId));
}

// ── Code / changeset links ────────────────────────────────────────────────

export interface TaskChangeset {
  Id: string;
  TaskId: string;
  Changeset: string;
  VcsChangesetId: string | null;
  Branch: string | null;
  AuthorName: string | null;
  AuthorEmail: string | null;
  VcsTimestamp: string | null;
  Comment: string;
  StatusIdAtRecord: string | null;
  RecordedByUserId: string | null;
  RecordedByName: string;
  CreatedAt: string;
}

export function listTaskChangesets(taskId: string): Promise<{ items: TaskChangeset[] }> {
  return json(buildPath(DASHBOARD_PATHS.tasksByIdChangesets, { id: taskId }));
}

export function recordTaskChangeset(
  taskId: string,
  body: { changeset: string; comment: string; statusId?: string | null },
): Promise<TaskChangeset> {
  invalidateTaskDetail(taskId);
  return json<TaskChangeset>(buildPath(DASHBOARD_PATHS.tasksByIdChangesets, { id: taskId }), {
    method: 'POST',
    body: JSON.stringify(body),
  }).finally(() => invalidateTaskDetail(taskId));
}

// ── Issue cross-links ────────────────────────────────────────────────────────

// Issue-link endpoints removed server-side. Stubs kept for call-site
// compatibility while the web-side migration is in flight. Each rejects
// loudly so unintended invocations surface during QA rather than 404ing.
export function addIssueLink(
  _taskId: string,
  _issueType: IssueLinkType,
  _issueId: string,
  _relation: IssueLinkRelation = 'related',
): Promise<TaskIssueLink> {
  return Promise.reject(new Error('addIssueLink: endpoint removed'));
}

export function removeIssueLink(_taskId: string, _linkId: string): Promise<{ ok: boolean }> {
  return Promise.reject(new Error('removeIssueLink: endpoint removed'));
}

// ── Comments (polymorphic issue_comments with issue_type='task') ───────────

export function listComments(
  taskId: string,
  opts: { linked?: boolean } = {},
): Promise<{ items: TaskComment[] }> {
  const qs = opts.linked ? '?linked=1' : '';
  return json(`${buildPath(DASHBOARD_PATHS.issuesByTypeComments, { type: 'task', id: taskId })}${qs}`);
}

export interface IncomingQuestionItem {
  taskId: string;
  commentId: string;
  author: string;
  authorEmail: string;
  body: string;
  createdAt: string;
  syncSource: 'bugpunch' | 'chat' | 'email';
}

export function fetchIncomingQuestions(
  taskIds: string[],
): Promise<{ items: IncomingQuestionItem[] }> {
  return json(DASHBOARD_PATHS.tasksIncomingQuestions, {
    method: 'POST',
    body: JSON.stringify({ taskIds }),
  });
}

export function postComment(taskId: string, body: string, visibility: 'internal' | 'public'):
  Promise<TaskComment & { notifiedVia?: 'email' | 'chat' | 'none'; fanoutError?: string }> {
  return json(buildPath(DASHBOARD_PATHS.issuesByTypeComments, { type: "task", id: taskId }), {
    method: 'POST',
    body: JSON.stringify({ body, visibility }),
  });
}

export function updateComment(taskId: string, commentId: string, body: string): Promise<TaskComment> {
  return json(buildPath(DASHBOARD_PATHS.issuesByTypeCommentsByCommentId, { type: "task", id: taskId, commentId }), {
    method: 'PATCH',
    body: JSON.stringify({ body }),
  });
}

export function deleteComment(taskId: string, commentId: string): Promise<{ ok: boolean }> {
  return json(buildPath(DASHBOARD_PATHS.issuesByTypeCommentsByCommentId, { type: "task", id: taskId, commentId }), { method: 'DELETE' });
}

export function toggleIssueCommentReaction(
  type: CommentIssueType,
  id: string,
  commentId: string,
  emoji: string,
): Promise<{ reactions: TaskCommentReaction[] }> {
  return json(buildPath(DASHBOARD_PATHS.issuesByTypeCommentsByCommentIdReactions, { type, id, commentId }), {
    method: 'POST',
    body: JSON.stringify({ emoji }),
  });
}

// ── Assignees ───────────────────────────────────────────────────────────────

export function listAssignees(options: TaskRequestOptions = {}): Promise<{ items: Assignee[] }> {
  return json(DASHBOARD_PATHS.tasksMetaAssignees, undefined, options);
}

// ── Subscriptions ──────────────────────────────────────────────────────────

// ── Saved searches ─────────────────────────────────────────────────────────

export type SavedSearchScope = 'personal' | 'project';
export type SavedSearchSurface = 'tasks' | 'issues';

export interface SavedSearch {
  Id: string;
  ProjectId: string | null;
  UserId: string;
  Scope: SavedSearchScope;
  Surface: SavedSearchSurface;
  Name: string;
  Query: string;
  IsDefault: boolean;
  SortOrder: number;
  CreatedAt: string;
}

export function listSavedSearches(): Promise<{ items: SavedSearch[] }> {
  return json(DASHBOARD_PATHS.tasksSavedSearches);
}
export function createSavedSearch(body: {
  name: string; query: string; scope: SavedSearchScope;
}): Promise<SavedSearch> {
  return json(DASHBOARD_PATHS.tasksSavedSearches, { method: 'POST', body: JSON.stringify(body) });
}
export function updateSavedSearch(id: string, body: Partial<{
  name: string; query: string; scope: SavedSearchScope; sortOrder: number;
}>): Promise<SavedSearch> {
  return json(buildPath(DASHBOARD_PATHS.tasksSavedSearchesById, { id }), { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteSavedSearch(id: string): Promise<{ ok: boolean }> {
  return json(buildPath(DASHBOARD_PATHS.tasksSavedSearchesById, { id }), { method: 'DELETE' });
}

// Flip the user's per-surface default chip for the active project. Pass
// `targetId=null` to clear the default. Server clears sibling defaults first
// so the "at most one default per (user, project, surface)" invariant holds.
export function setDefaultSavedSearch(
  targetId: string | null,
  surface: SavedSearchSurface = 'tasks',
): Promise<SavedSearch | { Id: null }> {
  const path = surface === 'issues'
    ? DASHBOARD_PATHS.issuesSavedSearchesDefault
    : DASHBOARD_PATHS.tasksSavedSearchesDefault;
  return json(path, { method: 'PUT', body: JSON.stringify({ targetId }) });
}

// ── Issues saved searches (same shape, different surface) ────────────────

export function listIssueSavedSearches(): Promise<{ items: SavedSearch[] }> {
  return json(DASHBOARD_PATHS.issuesSavedSearches);
}
export function createIssueSavedSearch(body: {
  name: string; query: string; scope: SavedSearchScope;
}): Promise<SavedSearch> {
  return json(DASHBOARD_PATHS.issuesSavedSearches, { method: 'POST', body: JSON.stringify(body) });
}
export function updateIssueSavedSearch(id: string, body: Partial<{
  name: string; query: string; scope: SavedSearchScope; sortOrder: number;
}>): Promise<SavedSearch> {
  return json(buildPath(DASHBOARD_PATHS.issuesSavedSearchesById, { id }), { method: 'PATCH', body: JSON.stringify(body) });
}
export function deleteIssueSavedSearch(id: string): Promise<{ ok: boolean }> {
  return json(buildPath(DASHBOARD_PATHS.issuesSavedSearchesById, { id }), { method: 'DELETE' });
}

// ── Notifications ──────────────────────────────────────────────────────────

export interface Notification {
  Id: string;
  Kind:
    | 'task_assigned' | 'task_status_changed' | 'task_commented'
    | 'task_mentioned' | 'requester_replied' | 'task_attachment_added'
    | 'chat_mentioned' | 'automation_run_finished' | 'automation_run_stalled'
    | 'automation_run_cancelled' | string;
  TaskId: string | null;
  CommentId: string | null;
  ChatThreadId: string | null;
  ChatMessageId: string | null;
  /** A dashboard path to open, such as /testing/sessions/<id>. */
  Link: string | null;
  ActorUserId: string | null;
  ActorName: string;
  Summary: string;
  Read: boolean;
  ReadAt: string | null;
  CreatedAt: string;
}

export function listNotifications(opts: { unread?: boolean; limit?: number } = {}):
  Promise<{ items: Notification[]; unreadCount: number }> {
  const qs = new URLSearchParams();
  if (opts.unread) qs.set('unread', '1');
  if (opts.limit) qs.set('limit', String(opts.limit));
  return json(qs.toString() ? `${DASHBOARD_PATHS.notifications}?${qs}` : DASHBOARD_PATHS.notifications);
}
export function unreadCount(): Promise<{ unreadCount: number }> {
  return json(DASHBOARD_PATHS.notificationsUnreadCount);
}
export function markNotificationsRead(ids?: string[]): Promise<{ updated: number; unreadCount: number }> {
  return json(DASHBOARD_PATHS.notificationsMarkRead, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
}
